import React from 'react';
import StaffProfileTemplate, {
  ProfileSection,
  ProfileParagraphs,
  ProfileBullets,
  ProfilePublications,
  ProfileContactSection,
} from './StaffProfileTemplate';

const TumuzoKatiryProfile = () => {
  const person = {
    id: 'tumuzo-katiry',
    name: 'Tümüzo Katiry',
    honorific: 'Mr',
    title: 'Assistant Researcher',
    affiliation: 'The Highland Institute, Kohima, Nagaland',
    image: 'https://customer-assets.emergentagent.com/job_vegr-highland/artifacts/38ck72jl_Tu%CC%88mu%CC%88zo%20Katiry.jpg',
    email: 'tumuzo@highlandinstitute.org',
    category: 'Research Staff',
    testid: 'tumuzo-katiry-profile',
  };

  return (
    <StaffProfileTemplate person={person}>
      <ProfileSection title="Intellectual Biography">
        <ProfileParagraphs
          items={[
            'Tümüzo Katiry is an Indigenous Naga researcher, ethnographic practitioner, and scholar in the environmental humanities. Based in Nagaland and associated with The Highland Institute in Kohima, his work engages with Indigenous knowledge systems, climate change, religion, and decolonial inquiry in the Indo-Myanmar highlands. He explores questions of Indigenous sovereignty, ecological futures, cultural memory, and the politics of knowledge production.',
            "His academic training includes a Master's degree in Anthropology from Kohima Science College, Jotsoma. For his dissertation, Katiry examined the animist belief system in Meluri district, Nagaland, through collaboration with the last generation of ancestral religious practitioners. This research contributes to understandings of Naga Indigenous cosmology and considers how proselytisation has reshaped the religious landscape of the region.",
            "Katiry's scholarship belongs to a wider movement among Indigenous and borderland researchers who are rethinking the frameworks through which Northeast India and the Naga world have historically been studied. Drawing on ethnography, environmental humanities, oral traditions, and Indigenous philosophy, his work examines how Indigenous communities sustain continuity, adapt to change, and represent themselves amid colonial borders, religious transformation, ecological uncertainty, and modern state formation. These concerns have been deepened through international engagements across the Himalayan and Southeast Asian regions, including the 2024 \"Understanding Risk Field Lab on Himalayan Climate Data\" in Kathmandu and the 2025 Decolonizing Southeast Asian Studies Conference at Chiang Mai University, where he presented on collaborative research innovations at The Highland Institute.",
          ]}
        />
        <p className="text-stone-700 font-medium mt-6 mb-4">Key areas of work and interest include:</p>
        <ProfileBullets
          items={[
            'Indigenous cosmologies and religious transformation',
            'Borderland anthropology and colonial cartographies',
            'Decolonial and community-engaged research methodologies',
            'Environmental humanities and Indigenous ecological knowledge',
            'Anthropology of religion in the Indo-Myanmar highlands',
            'Sovereignty, customary governance, and Indigenous institutions',
            'Climate adaptation, food systems, and ecological futures',
            'Oral traditions, memory, and cultural continuity',
          ]}
        />
      </ProfileSection>

      <ProfileSection title="Research Projects and Collaborations">
        <ProfileParagraphs
          items={[
            "Tümüzo Katiry was a Canada-IDRC Myanmar Research Fellow on the Earthkeepers Project, conducted by The Highland Institute in partnership with the International Development Research Centre (IDRC), Canada. The project examined traditional ecological knowledge and climate resilience among Indigenous communities in the Indo–Myanmar borderlands. Through ethnographic fieldwork in areas such as Noklak and Meluri, in Eastern Nagaland, it explored how local communities understand environmental change through farming practices, forest relations, water systems, and customary institutions. Katiry's contribution emphasised community-engaged approaches to climate research and the preservation of ecological knowledge systems often marginalised within mainstream developmental discourse.",
            "Building on this work at the intersection of Indigenous knowledge, climate change, and community-based research, Katiry is currently engaged in an International Climate Protection Fellowship Project funded by the Alexander von Humboldt Foundation, hosted at the Geographisches Institut, Humboldt-Universität zu Berlin, Germany. The project focuses on understanding the role of Nagaland's Indigenous youth organisations in conservation and climate change efforts, as well as the challenges they face in accessing support, resources, and opportunities.",
            "His wider engagement with ecological futures also extends into questions of food, adaptation, and cultural practice. Katiry is involved in The Highland Institute's contribution to the global Tasting Tomorrow project, conceived by Jonathon Keats. At The Highland Institute, the project examined food practices, adaptive culinary knowledge, and environmental change within the Naga context. It approached cuisine not only as a form of cultural expression, but also as a living archive of ecological adaptation and intergenerational knowledge transmission, linking everyday food practices to wider debates in environmental anthropology and climate adaptation studies.",
          ]}
        />
      </ProfileSection>

      <ProfileSection title="Academic Publications and Reports">
        <ProfilePublications
          items={[
            {
              year: '2024',
              citation: (
                <>
                  'Tasting Tomorrow: Exploring climate-adapted cuisines in Nagaland' in <em>The Highlander Journal</em>, Vol. 3, Issue 2, pp. 73–75.
                </>
              ),
              url: 'https://doi.org/10.5281/zenodo.11001758',
            },
            {
              year: '2024',
              citation: (
                <>
                  'Earthkeepers Project: Noklak Border Mission' in <em>Highlander</em>, Vol. 3, Issue 2, pp. 116–124.
                </>
              ),
              url: 'https://doi.org/10.5281/zenodo.11002249',
            },
            {
              year: '2025',
              citation: (
                <>
                  'Traditional ecological knowledge and climate resilience in Eastern Nagaland: Insights from the Earthkeepers Project 2023–2025' unpublished report, Earthkeepers Project, The Highland Institute.
                </>
              ),
            },
          ]}
        />
        <h3 className="font-semibold text-stone-900 mt-8 mb-3 not-prose">Blogs</h3>
        <ul className="space-y-3 not-prose">
          <li className="text-stone-700 pl-4 border-l-2 border-green-600">
            <a
              href="https://borderlab.eu/blog/sleep-in-india-reap-in-myanmar-how-villagers-defy-division-on-a-porous-border/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-green-700 hover:text-green-800 underline break-all"
            >
              Sleep in India; Reap in Myanmar: how villagers defy division on a porous border
            </a>
          </li>
          <li className="text-stone-700 pl-4 border-l-2 border-green-600">
            <a
              href="https://blogs.highlanderpress.org/index.php/2026/02/09/bothered-by-the-border/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-green-700 hover:text-green-800 underline break-all"
            >
              Bothered by the Border
            </a>
          </li>
          <li className="text-stone-700 pl-4 border-l-2 border-green-600">
            <a
              href="https://blogs.highlanderpress.org/index.php/2026/02/18/nyavu-khu-cultural-revitalisation-of-lephori-village/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-green-700 hover:text-green-800 underline break-all"
            >
              Nyavu Khu: Cultural Revitalisation of Lephori Village
            </a>
          </li>
        </ul>
      </ProfileSection>

      <ProfileSection title="Postgraduate Qualification">
        <ProfileBullets items={['MSc in Anthropology']} />
      </ProfileSection>

      <ProfileContactSection
        email={person.email}
        role="Assistant Researcher, The Highland Institute"
        testidPrefix="tumuzo"
        scholarlyProfiles={[
          { label: 'ORCID', url: 'https://orcid.org/0009-0006-7673-2883', testid: 'tumuzo-orcid-link' },
          { label: 'Academia.edu', url: 'https://independent.academia.edu/T%C3%BCm%C3%BCzoKatiry', testid: 'tumuzo-academia-link' },
          { label: 'LinkedIn', url: 'https://in.linkedin.com/in/t%C3%BCm%C3%BCzo-katiry-16588a249', testid: 'tumuzo-linkedin-link' },
        ]}
      />
    </StaffProfileTemplate>
  );
};

export default TumuzoKatiryProfile;
